import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { CurrentPlan } from '../../components/billing/CurrentPlan';
import { UsageMetrics } from '../../components/billing/UsageMetrics';
import { BillingHistory } from '../../components/billing/BillingHistory';
import { PlanComparison } from '../../components/billing/PlanComparison';
import { useBilling } from '../../hooks/useBilling';
import { 
  CreditCard, 
  Receipt, 
  TrendingUp, 
  Crown,
  AlertCircle,
  CheckCircle,
  Calendar,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export function BillingPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const { 
    useSubscription, 
    useUsage, 
    useProducts, 
    useInvoices,
    createPortalSession, 
    syncSubscription,
    downloadInvoice,
    handlePortalReturn,
    isCreatingPortal, 
    isSyncing,
    isDownloadingInvoice
  } = useBilling();

  // Fetch real data
  const { data: subscription, isLoading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const { data: usage, isLoading: usageLoading } = useUsage();
  const { data: products, isLoading: productsLoading } = useProducts();
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({ limit: 5 });

  // Handle portal return and auto-sync
  useEffect(() => {
    // Check if user returned from portal and sync if needed
    const syncTriggered = handlePortalReturn();
    if (syncTriggered) {
      console.log('Auto-syncing subscription data after portal visit');
    }
  }, [handlePortalReturn]);

  const handleManageSubscription = async () => {
    try {
      await createPortalSession({
        returnUrl: window.location.href
      });
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const handleSyncSubscription = async () => {
    try {
      await syncSubscription();
    } catch (error) {
      console.error('Failed to sync subscription:', error);
    }
  };

  const handleDownloadLatestInvoice = async () => {
    try {
      if (invoicesData?.invoices?.[0]?.id) {
        await downloadInvoice(invoicesData.invoices[0].id);
      } else {
        toast.error('No invoices available for download');
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  if (subscriptionLoading || usageLoading || productsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading billing information...</span>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load billing information</h3>
        <p className="text-gray-600">Please try refreshing the page or contact support if the issue persists.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
          <p className="text-gray-600 mt-1">
            Manage your subscription and monitor usage
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline"
            onClick={handleSyncSubscription}
            disabled={isSyncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Data'}
          </Button>
          <Button 
            variant="outline"
            onClick={handleDownloadLatestInvoice}
            disabled={isDownloadingInvoice || !invoicesData?.invoices?.[0]}
          >
            <Receipt className="w-4 h-4 mr-2" />
            {isDownloadingInvoice ? 'Downloading...' : 'Download Invoice'}
          </Button>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Plan
          </Button>
        </div>
      </div>

      {/* Current Plan Status */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-4">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {subscription?.product?.name || 'Free Plan'}
                </h3>
                <Badge variant={subscription?.organization?.subscription_status === 'active' ? 'default' : 'secondary'}>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {subscription?.organization?.subscription_status || 'Trial'}
                  {subscription?.organization?.subscription_status === 'trial' && subscription?.organization?.trial_ends_at && (
                    <span className="ml-1">
                      • ends {new Date(subscription.organization.trial_ends_at).toLocaleDateString()}
                    </span>
                  )}
                </Badge>
              </div>
              <p className="text-gray-600">
                {subscription?.product ? (
                  <>
                    ${(subscription.product.price_cents / 100).toFixed(2)}/{subscription.product.billing_interval}
                    {subscription.organization.current_period_end && (
                      <> • Next billing: {new Date(subscription.organization.current_period_end).toLocaleDateString()}</>
                    )}
                  </>
                ) : (
                  'Free trial'
                )}
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleManageSubscription}
            disabled={isCreatingPortal}
          >
            {isCreatingPortal ? 'Loading...' : 'Manage Subscription'}
          </Button>
        </div>
      </Card>

      {/* Sync Status Alert */}
      {isSyncing && (
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex items-center">
            <Loader2 className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">
                Syncing subscription data from Stripe...
              </p>
              <p className="text-sm text-blue-600">
                This will ensure your latest plan changes are reflected here.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Usage Alert */}
      {usage && usage.usage && (
        <Card className="p-4 border-orange-200 bg-orange-50">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-orange-600 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">
                Monitor your usage to avoid service interruption
              </p>
              <p className="text-sm text-orange-600">
                Check your current usage limits and upgrade if needed
              </p>
            </div>
            <Button size="sm" variant="outline">
              View Usage
            </Button>
          </div>
        </Card>
      )}

      {/* Billing Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="usage" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center">
            <Receipt className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center">
            <Crown className="w-4 h-4 mr-2" />
            Plans
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CurrentPlan 
              plan={{
                name: subscription?.product?.name || 'Free Plan',
                price: subscription?.product ? subscription.product.price_cents / 100 : 0,
                billingCycle: subscription?.product?.billing_interval || 'month',
                status: subscription?.organization?.subscription_status || 'trial',
                nextBilling: subscription?.organization?.current_period_end || null,
                trialEndsAt: subscription?.organization?.trial_ends_at || null,
                features: subscription?.product?.product_features?.map((f: any) => f.feature_name) || []
              }} 
            />
            <UsageMetrics 
              usage={{
                apiCalls: { 
                  used: usage?.currentCounts?.api_calls || 0, 
                  limit: usage?.limits?.max_api_calls || 2000 
                },
                aiTokens: { 
                  used: usage?.currentCounts?.ai_tokens || 0, 
                  limit: usage?.limits?.max_ai_requests || 20000 
                },
                projects: { 
                  used: usage?.currentCounts?.projects || 0, 
                  limit: usage?.limits?.max_projects || 3 
                },
                seats: { 
                  used: usage?.currentCounts?.team_members || 0, 
                  limit: usage?.limits?.max_team_members || 3 
                }
              }} 
            />
          </div>
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <UsageMetrics 
            usage={{
              apiCalls: { 
                used: usage?.currentCounts?.api_calls || 0, 
                limit: usage?.limits?.max_api_calls || 2000 
              },
                              aiTokens: { 
                  used: usage?.currentCounts?.ai_tokens || 0, 
                  limit: usage?.limits?.max_ai_requests || 20000 
                },
              projects: { 
                used: usage?.currentCounts?.projects || 0, 
                limit: usage?.limits?.max_projects || 3 
              },
              seats: { 
                used: usage?.currentCounts?.team_members || 0, 
                limit: usage?.limits?.max_team_members || 3 
              }
            }} 
            detailed 
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <BillingHistory invoices={invoicesData?.invoices || []} />
        </TabsContent>

        <TabsContent value="plans" className="space-y-6">
          <PlanComparison 
            currentPlan={subscription?.product?.name || 'Free Plan'}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}